import { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { TID } from "@/lib/testIds";
import Logo from "@/components/Logo";
import { toast } from "sonner";
import { ArrowUpRight, Check, Sparkles, Lock } from "lucide-react";

export default function Signup() {
  const { signup } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [whatsapp, setWhatsapp] = useState("");
  const [pw, setPw] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [refCtx, setRefCtx] = useState(null);  // {valid, host_university, host_city, country, referrer_display_name}
  // iter93 (Item LL) — mandatory T&C + Privacy consent. Unchecked by default
  // (genuine opt-in, no dark pattern). Submit is disabled until this is true.
  // Acceptance timestamp + version is sent to the backend so there's a real
  // server-side record of consent, not just a UI gate.
  const [accepted, setAccepted] = useState(false);
  const TERMS_VERSION = "2026-07-27";

  // iter96 (PP) — signup attribution. Capture ?src=… on any /signup URL and
  // persist it into sessionStorage so it survives the intake flow (same
  // pattern as ?ref=). Default "direct" is applied server-side, not here, so
  // the field is never null on the user record.
  const [source, setSource] = useState(null);
  useEffect(() => {
    const s = params.get("src");
    if (s) sessionStorage.setItem("moe_src", s.trim().toLowerCase().slice(0, 64));
  }, [params]);

  // iter94 (Cohort P1) — WhatsApp number is required at signup. Basic client-
  // side check: strip everything but digits, need 8–20 characters. Backend
  // re-validates the same rule (fail-closed).
  const waDigits = whatsapp.replace(/[^\d]/g, "");
  const waValid  = waDigits.length >= 8 && waDigits.length <= 20;
  const canSubmit = accepted && waValid && !busy;

  // Referral capture — ?ref=CODE on /signup or /start persists across the flow
  useEffect(() => {
    const code = params.get("ref");
    if (!code) return;
    const upper = code.trim().toUpperCase();
    sessionStorage.setItem("moe_ref", upper);
    api.get(`/referral/lookup?code=${encodeURIComponent(upper)}`)
      .then((r) => setRefCtx(r.data))
      .catch(() => setRefCtx({ valid: false }));
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const resp = await signup(name, email, pw, {
        whatsapp: whatsapp.trim(),
        terms_accepted: true,
        terms_version: TERMS_VERSION,
        // iter96 (PP) — attribution: read the captured ?src=… from
        // sessionStorage. Backend normalises + defaults to "direct" if unset.
        source: sessionStorage.getItem("moe_src") || null,
      });
      // Institutional email verification signal — surface "pending review" once,
      // via toast, before routing to intake. NEVER blocks the signup.
      if (resp?.institutional_status === "pending_admin_review") {
        toast.warning(
          "Signed up. But we couldn't verify your school domain automatically. An admin will review it.",
          { duration: 6000 }
        );
      } else if (resp?.institutional_school_short) {
        toast.success(`Verified as ${resp.institutional_school_short}`);
      }
      nav("/start");
    } catch (e) {
      const msg = formatApiError(e.response?.data?.detail) || e.message;
      setErr(msg); toast.error(msg);
    } finally { setBusy(false); }
  };

  const perks = [
    "Visa path decided in 60 seconds",
    "Personal timeline with real deadlines",
    "19 country dossiers open to browse",
    "See who else from your school is going",
  ];

  return (
    <div className="min-h-[calc(100vh-80px)] grid md:grid-cols-2">
      <div className="hidden md:flex flex-col justify-between p-10 moe-panel-mint">
        <Logo size="md"/>
        <div>
          <div className="text-xs uppercase tracking-widest font-semibold">Start free</div>
          <h2 className="mt-4 text-4xl lg:text-5xl font-bold tracking-tight text-ink">Five questions.<br/>Real plan.</h2>
          <ul className="mt-8 space-y-3">
            {perks.map(p => (
              <li key={p} className="flex items-center gap-2 text-sm font-medium text-ink">
                <Check size={16} className="text-elec"/>{p}
              </li>
            ))}
          </ul>
        </div>
        <div className="text-xs text-ink/70">No card. No payment. Just the plan.</div>
      </div>

      <div className="p-6 md:p-12 flex items-center">
        <div className="w-full max-w-sm mx-auto">
          <div className="md:hidden mb-8"><Logo/></div>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight">Create account</h1>
          <p className="mt-2 text-sm text-muted">Two minutes to intake.</p>

          {refCtx?.valid && (
            <div
              className="mt-5 flex items-start gap-2.5 p-3.5 rounded-xl bg-[color:var(--moe-yolk)]/40 border border-[color:var(--moe-yolk)]"
              data-testid="signup-ref-context"
            >
              <Sparkles size={16} className="mt-0.5 text-ink shrink-0" strokeWidth={2.4} />
              <div className="text-[13px] leading-snug text-ink">
                {refCtx.referrer_display_name ? (
                  <><span className="font-bold">{refCtx.referrer_display_name}</span> invited you</>
                ) : (
                  <span className="font-bold">You've been invited</span>
                )}
                {refCtx.host_university && (
                  <>. Headed to <span className="font-bold">{refCtx.host_university}</span></>
                )}
                {refCtx.country && !refCtx.host_university && (
                  <>. Headed to <span className="font-bold">{refCtx.country}</span></>
                )}
                {". Sign up and you'll land right in the same cohort."}
              </div>
            </div>
          )}

          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="moe-field">
              <input data-testid={TID.signupName} required value={name} onChange={(e)=>setName(e.target.value)} placeholder=" " />
              <label>Full name</label>
            </div>
            <div className="moe-field">
              <input data-testid={TID.signupEmail} type="email" required value={email} onChange={(e)=>setEmail(e.target.value)} placeholder=" " />
              <label>Institutional email</label>
            </div>
            <p className="-mt-3 text-[11px] text-muted leading-snug" data-testid="signup-institutional-hint">
              Use your B-school email (e.g. <span className="font-semibold text-ink">yourname@iimb.ac.in</span>).
              If we can't verify your school, an admin reviews the account. You won't be locked out.
            </p>
            <div className="moe-field">
              <input
                data-testid={TID.signupWhatsapp}
                type="tel"
                required
                inputMode="tel"
                autoComplete="tel"
                value={whatsapp}
                onChange={(e)=>setWhatsapp(e.target.value)}
                placeholder=" "
              />
              <label>WhatsApp number (with country code)</label>
            </div>
            <p className="mt-1 text-[11px] text-muted leading-snug" data-testid="signup-whatsapp-hint">
              <Lock size={11} className="inline-block mr-1 -mt-0.5 text-elec" strokeWidth={2.25}/>
              Hidden until you both accept a connection. Never shown otherwise.
              <span className="block text-[10.5px] text-muted/85 mt-0.5">
                Include your country code (e.g. <span className="font-semibold text-ink">+91 98123 45678</span>).
              </span>
            </p>
            <div className="moe-field">
              <input data-testid={TID.signupPassword} type="password" required minLength={6} value={pw} onChange={(e)=>setPw(e.target.value)} placeholder=" " />
              <label>Password (min 6)</label>
            </div>
            {err && <div className="text-sm text-red-600">{err}</div>}

            {/* iter99 (BBB.1) — quiet contextual trust line above the mandatory
                Terms checkbox. Small, muted, non-competing. */}
            <div
              className="flex items-start gap-1.5 text-[11px] text-muted leading-snug -mt-1"
              data-testid="signup-trust-line"
            >
              <Lock size={11} className="mt-0.5 shrink-0 text-elec" strokeWidth={2.25}/>
              <span>
                Your data is encrypted and never sold.{" "}
                <Link to="/security" target="_blank" rel="noopener" data-testid="signup-security-link" className="text-elec font-semibold underline">
                  Learn more
                </Link>
              </span>
            </div>

            {/* iter93 (Item LL) — mandatory consent. Unchecked by default. */}
            <label
              className="flex items-start gap-2 text-[13px] text-ink/85 leading-snug select-none cursor-pointer"
              data-testid="signup-terms-row"
            >
              <input
                type="checkbox"
                data-testid="signup-terms-checkbox"
                checked={accepted}
                onChange={(e) => setAccepted(e.target.checked)}
                className="mt-0.5 w-4 h-4 accent-[color:var(--moe-elec)] shrink-0"
              />
              <span>
                I agree to the
                {" "}<Link to="/terms" target="_blank" rel="noopener" data-testid="signup-terms-link" className="text-elec font-semibold underline">Terms &amp; Conditions</Link>
                {" "}and the
                {" "}<Link to="/privacy" target="_blank" rel="noopener" data-testid="signup-privacy-link" className="text-elec font-semibold underline">Privacy Policy</Link>.
              </span>
            </label>

            <button
              data-testid={TID.signupSubmit}
              disabled={!canSubmit}
              className="moe-btn moe-btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {busy ? "Creating…" : <>Create account <ArrowUpRight size={15}/></>}
            </button>
          </form>
          <div className="mt-6 text-sm text-muted">
            Already have an account? <Link to="/login" className="text-elec font-semibold">Log in →</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
